import { Dialog, DialogTitle } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import React from "react";
import { Edit } from "./Edit";
import { Approve } from "./Approve";
import {
  PlanId,
  usePlanId,
  useSetPlanId,
} from "components/common/boundary/PlanId";
import { Success } from "./Success";

export enum State {
  Edit,
  Approve,
  Success,
}

const toNextState = (state: State): State.Approve | State.Success => {
  switch (state) {
    case State.Edit:
      return State.Approve;
    case State.Approve:
    case State.Success:
      return State.Success;
  }
};

const toPreviousState = (state: State): State.Edit | State.Approve => {
  switch (state) {
    case State.Edit:
    case State.Approve:
      return State.Edit;
    case State.Success:
      return State.Approve;
  }
};

interface Props {
  open: boolean;
  doClose: () => void;
}

export const PurchasingPlanDeployModal = ({ open, doClose }: Props) => {
  const [state, setState] = React.useState(State.Edit);
  const { t } = useTenantTranslation();
  const planId = usePlanId();
  const setPlanId = useSetPlanId();

  const handleToNextState = React.useCallback(() => {
    setState(toNextState);
  }, [setState]);

  const handleToPreviousState = React.useCallback(() => {
    setState(toPreviousState);
  }, [setState]);

  const handleSetPlanId = React.useCallback(
    (planId: PlanId) => {
      setPlanId(planId, true);
    },
    [setPlanId]
  );

  const body = React.useMemo((): React.JSX.Element => {
    switch (state) {
      case State.Edit:
        return (
          <Edit
            toNextState={handleToNextState}
            planId={planId}
            setPlanId={handleSetPlanId}
            doClose={doClose}
          />
        );
      case State.Approve:
        return (
          <Approve
            toPreviousState={handleToPreviousState}
            toNextState={handleToNextState}
            planId={planId}
            doClose={doClose}
          />
        );
      case State.Success:
        return <Success doClose={doClose} />;
    }
  }, [
    state,
    handleToNextState,
    doClose,
    planId,
    handleSetPlanId,
    handleToPreviousState,
  ]);

  return (
    <Dialog
      open={open}
      onClose={doClose}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          // Fix to always be "full height" (i.e. 90vh)
          height: "90vh",
          maxWidth: "unset",
        },
      }}
    >
      <DialogTitle>{t("deployPlan")}</DialogTitle>
      {body}
    </Dialog>
  );
};
